import {
  isPromoCodeEnabled,
  isSubscriptionExpired,
} from "@circle-react/helpers/communityHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers/settingsNavPathV3";
import { useLocation } from "@tanstack/react-router";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";

export interface StandardLayoutV2WrapperProps extends ComponentPropsWithoutRef<"div"> {
  children: ReactNode;
  disableLayoutRightSidebar?: boolean;
  disableSidebar?: boolean;
  disableSidebarNestedBarLinks?: boolean;
  hasSettingsSidebar?: boolean;
  isFullHeight?: boolean;
}

export const StandardLayoutV2Wrapper = ({
  disableSidebar = false,
  disableSidebarNestedBarLinks = false,
  disableLayoutRightSidebar = false,
  hasSettingsSidebar = false,
  isFullHeight = false,
  children,
  className,
  ...rest
}: StandardLayoutV2WrapperProps) => {
  const pathname = useLocation({ select: location => location.pathname });
  const { currentCommunity } = usePunditUserContext();
  const isCommunityInboxPage = pathname.includes(
    settingsNavPathV3.community_inbox.index,
  );

  // This is needed for new plans page when community has cancelled subscription
  const shouldAppBeLocked =
    isSubscriptionExpired(currentCommunity) ||
    isPromoCodeEnabled(currentCommunity);

  const shouldDisableSideBarLinks =
    disableSidebarNestedBarLinks && !shouldAppBeLocked;

  return (
    <div
      className={classNames(
        anchorClassNames("standard-layout-v2", {
          "standard-layout-v2--has-sidebar": !disableSidebar,
          "standard-layout-v2--has-sidebar-v3": !disableSidebar,
          "standard-layout-v2--has-settings-sidebar":
            hasSettingsSidebar && !isCommunityInboxPage,
          "standard-layout-v2--no-right-sidebar": disableLayoutRightSidebar,
        }),
        "bg-secondary",
        isCommunityInboxPage &&
          hasSettingsSidebar &&
          legacyClassNames("standard-layout-v2--has-community-inbox-sidebar"),
        shouldDisableSideBarLinks &&
          legacyClassNames("standard-layout-v2--hidden-nested-links"),
        isFullHeight && "h-full",
        className,
      )}
      data-testid="standard-layout-v2-wrapper"
      {...rest}
    >
      {children}
    </div>
  );
};
